import { useDebounce } from 'hooks/useDebounce';
import type { FunctionComponent } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { isValidPhoneNumber } from 'react-phone-number-input';
import PhoneInput from 'react-phone-number-input/input';
// @ts-ignore
import en from 'react-phone-number-input/locale/en';
import { styled } from 'styled-components';

import CountrySelect from './CountrySelect';

const StyledDiv = styled.div`
  width: 47%;
`;

const StyledCol = styled(Col)`
  display: flex;
  gap: 8px;
`;

const StyledPhoneInput = styled(PhoneInput)<{ $isvalid?: 'true' | 'false' }>`
  width: 100%;
  height: 44px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.ghostWhite};
  background: ${({ theme }) => theme.colors.white};
  padding: 7px 10px;
  font-size: 16px;
  color: ${({ theme }) => theme.colors.black};
  outline: none;
  &:focus {
    border-color: ${({ $isvalid, theme }) => ($isvalid === 'true' ? theme.colors.success : theme.colors.deepPurple)};
  }
`;

interface Props {
  autoFocus?: boolean;
  defaultCountry?: string;
  placeholder?: string;
  onChange?: (phoneNumber: string) => void;
  defaultValue?: string;
  onCountryChange?: (country: string) => void;
}

const PhonePicker: FunctionComponent<Props> = ({ autoFocus, defaultCountry = 'AE', onCountryChange, placeholder = '', onChange, defaultValue }) => {
  const [country, setCountry] = useState<string>(defaultCountry);
  const [value, setValue] = useState<string | undefined>(defaultValue);
  const [isValid, setIsValid] = useState<boolean>(false);
  const debouncedPhone = useDebounce(value, 300);

  useEffect(() => {
    const validateAndTriggerOnChange = (phoneNumber: string) => {
      let isValidPhone = false;
      const checkIsValid = isValidPhoneNumber(phoneNumber ?? '');

      if (checkIsValid) {
        isValidPhone = true;
      } else {
        isValidPhone = false;
      }

      if (isValidPhone !== isValid) {
        setIsValid(isValidPhone);
      }

      if (!isValidPhone) {
        onChange?.('');
      } else {
        onChange?.(phoneNumber!!);
      }
    };

    if (debouncedPhone) {
      validateAndTriggerOnChange(debouncedPhone);
    }
  }, [debouncedPhone, isValid, onChange]);

  const handlePhoneChange = useCallback(
    (phoneNumber?: string) => {
      if (!phoneNumber) {
        onChange?.('');
      }

      setValue(phoneNumber);
    },
    [onChange]
  );

  const handleCountryChange = useCallback(
    (selectedCountry: string) => {
      setCountry(selectedCountry);
      onCountryChange?.(selectedCountry);
    },
    [onCountryChange]
  );

  return (
    <Row>
      <StyledCol>
        <StyledDiv>
          <CountrySelect labels={en} value={country} onChange={handleCountryChange} />
        </StyledDiv>
        <StyledPhoneInput
          country={country}
          value={value}
          onChange={handlePhoneChange}
          autoFocus={autoFocus}
          placeholder={placeholder}
          $isvalid={`${isValid}`}
        />
      </StyledCol>
    </Row>
  );
};

export default PhonePicker;
